import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Experience from './components/Experience';
import Skills from './components/Skills';
import Projects from './components/Projects';
import Education from './components/Education';
import Contact from './components/Contact';
import Footer from './components/Footer';
import FaultyTerminal from './components/FaultyTerminal';
import TargetCursor from './components/TargetCursor';
import SplitText from './components/SplitText';
import StackScroll from './components/StackScroll';
import ScrambledText from './components/ScrambledText';
import TrueFocus from './components/TrueFocus';

const AppContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #ffffff;
  position: relative;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const TerminalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.3;
  background: #000;
`;

const LoadingScreen = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const LoadingText = styled(motion.div)`
  font-size: clamp(1.5rem, 5vw, 2rem);
  font-weight: 700;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  text-align: center;
  padding: 0 1rem;
`;

const MainContent = styled(motion.div)`
  position: relative;
  z-index: 10;
`;

const App = () => {
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState('home');

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'experience', 'skills', 'projects', 'education', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setCurrentSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stackItems = [
    {
      title: "Welcome to My Portfolio",
      description: "Hello World! from Anas",
      background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
      overlay: "rgba(0, 0, 0, 0.3)",
      titleComponent: (
        <TrueFocus
          sentence="Welcome to My Portfolio"
          manualMode={false}
          blurAmount={3}
          borderColor="#00ff00"
          animationDuration={0.8}
          pauseBetweenAnimations={1.5}
        />
      ),
      descriptionComponent: (
        <ScrambledText
          radius={80}
          duration={1.0}
          speed={0.6}
          scrambleChars=".:"
          className="text-lg md:text-xl text-gray-300"
        >
          Hello World! from Anas
        </ScrambledText>
      )
    },
    {
      title: "Mern Stack Developer",
      description: "Creating innovative web solutions with cutting-edge technologies and modern design patterns",
      background: "linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)",
      overlay: "rgba(0, 0, 0, 0.4)",
      titleComponent: (
        <TrueFocus
          sentence="Mern Stack Developer"
          manualMode={false}
          blurAmount={3}
          borderColor="#00ff00"
          animationDuration={0.8}
          pauseBetweenAnimations={1.5}
        />
      ),
      descriptionComponent: (
        <ScrambledText
          radius={100}
          duration={1.2}
          speed={0.5}
          scrambleChars=".:"
          className="text-lg md:text-xl text-gray-300"
        >
          Creating innovative web solutions with cutting-edge technologies and modern design patterns
        </ScrambledText>
      )
    },
    {
      title: "AI Solutions Specialist",
      description: "Designs, develops, and implements AI-driven solutions to solve complex business challenges.",
      background: "linear-gradient(135deg, #0f0f0f 0%, #0a0a0a 100%)",
      overlay: "rgba(0, 0, 0, 0.5)",
      titleComponent: (
        <TrueFocus
          sentence="AI Solutions Specialist"
          manualMode={false}
          blurAmount={3}
          borderColor="#00ff00"
          animationDuration={0.8}
          pauseBetweenAnimations={1.5}
        />
      ),
      descriptionComponent: (
        <ScrambledText
          radius={120}
          duration={1.5}
          speed={0.4}
          scrambleChars=".:"
          className="text-lg md:text-xl text-gray-300"
        >
          Designs, develops, and implements AI-driven solutions to solve complex business challenges.
        </ScrambledText>
      )
    }
  ];

  return (
    <>
      <TargetCursor 
        spinDuration={3}
        hideDefaultCursor={false}
        targetSelector="a, button, input, textarea, [data-cursor-text], .cursor-target"
      />
      <AppContainer>
        <TerminalBackground>
          <FaultyTerminal
            scale={1.5}
            gridMul={[2, 1]}
            digitSize={1.2}
            timeScale={1}
            pause={false}
            scanlineIntensity={0.8}
            glitchAmount={1}
            flickerAmount={1}
            noiseAmp={1}
            chromaticAberration={0}
            dither={0}
            curvature={0}
            tint="#00ff00"
            mouseReact={true}
            mouseStrength={0.5}
            pageLoadAnimation={false}
            brightness={1}
          />
        </TerminalBackground>
        
        <AnimatePresence>
          {loading && (
            <LoadingScreen
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <LoadingText
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <SplitText type="characters" animation="typewriter" stagger={0.1}>
                  Loading Anas Butt Portfolio...
                </SplitText>
              </LoadingText>
            </LoadingScreen>
          )}
        </AnimatePresence>

        <MainContent
          initial={{ opacity: 0 }}
          animate={{ opacity: loading ? 0 : 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Header currentSection={currentSection} />
          
          <StackScroll items={stackItems} />
          
          <Hero />
          <About />
          <Experience />
          <Skills />
          <Projects />
          <Education />
          <Contact />
          <Footer />
        </MainContent>
      </AppContainer>
    </>
  );
};

export default App;
